// The range, as presented in navigation and on the range grids (v2 redesign).
//
// One source of truth for the nav mega-menus, the Home "Shop the range" grid and
// the Products index, so a product can never appear in one place and not another.
// `fit` is the object-fit for the thumbnail: product renders are contained on
// their white ground, photography is covered.

import { CONCRETE_MOUNT } from "./concreteMount";
import { EXTENSIONS, EXTENSION_KEYS, extensionFromPrice } from "./fenceExtensions";
import { PREMIUM_TIERS, FLAT_TIERS, formatAUD } from "../lib/pricing";

// Card prices read from the same tier tables the configurator charges from, so
// a price change in lib/pricing.js reaches the cards without a second edit.
const fromTiers = (tiers) => {
  const best = tiers[tiers.length - 1];
  return { price: formatAUD(tiers[0].unit), unit: `each · ${formatAUD(best.unit)} at ${best.min}+` };
};
const EXTENSION_FROM = Math.min(...EXTENSION_KEYS.map((k) => extensionFromPrice(EXTENSIONS[k])));

export const SUPPORTED_BRACKET = {
  to: "/products/supported-bracket",
  name: "Supported Slide-On Bracket",
  note: "Angle-backed, with timber support lip",
  img: "/images/staketek-supported-bracket-studio-gal-angled.jpg",
  fit: "contain",
};

export const FLAT_BRACKET_ITEM = {
  to: "/products/flat-bracket",
  name: "Flat Slide-On Bracket",
  note: "Flat fixing face",
  img: "/images/staketek-flat-bracket-white-1.jpg",
  fit: "contain",
};

export const INSTALLATION_KITS_ITEM = {
  to: "/products/installation-kits",
  name: "Installation Kits",
  note: "Matched screws, bolts and nuts",
  img: "/images/kit-install-tight.jpg",
  fit: "cover",
};

export const BULK_PACKS_ITEM = {
  to: "/products/bulk-packs",
  name: "Bulk Packs",
  // The first discounted band, read from the tiers (1–19 is full price).
  note: `Volume pricing from ${PREMIUM_TIERS[1].min} brackets`,
  img: "/images/job-run-portrait-1200.jpg",
  fit: "cover",
};

export const FENCE_POSTS_ITEM = {
  to: "/posts",
  name: "Fence Posts",
  note: "Tube-type pickets — separate system",
  img: "/images/hero-eucalyptus.jpg",
  fit: "cover",
};

export const ANIMAL_EXTENSION_ITEM = {
  to: "/products/animal-hurdle",
  name: "27° Animal Extension",
  note: "Raises and angles the top course",
  img: "/images/studio-post-animal-extension.jpg",
  fit: "cover",
};

// A neutral scenic fence photo (not a product shot) — final extension
// photography is pending, so this deliberately shows the use context, the same
// way the posts/animal cards use scenic imagery rather than a misleading render.
export const FENCE_EXTENSIONS_ITEM = {
  to: "/products/fence-height-extensions",
  name: "Fence Extensions & Joiners",
  note: "Raise height or join a picket. Gal or aluminium",
  img: "/images/fence-paddock.jpg",
  fit: "cover",
};

// Extra nav items for the category-led "Products" mega-menu.
export const NOTCHED_POST_ITEM = {
  to: "/posts/notched-groove", name: "Notched-Groove Post", note: "One continuous high-tensile tie",
};
export const TUBE_POST_ITEM = {
  to: "/posts/tube-post", name: "Tube Post", note: "Traditional holes fixing method",
};
export const STAR_PICKET_ANIMAL_ITEM = {
  to: "/products/star-picket-animal-extension", name: "Star Picket Animal Extension",
  note: "Angled exclusion top, slides over a star picket",
};
export const ANIMAL_EXTENSIONS_COLLECTION_ITEM = {
  to: "/products/animal-extensions", name: "Angled Animal Extensions",
  note: "Angled star-picket and tube exclusion tops",
};

// New products + the applications hub, as nav rows.
// The concrete-mount base is part of the BRACKET family — a ground/base bracket
// that mounts the picket to concrete rather than sliding onto it.
export const CONCRETE_MOUNT_ITEM = {
  to: "/products/anchor-bracket", name: "Anchor Base Bracket",
  note: "Anchors a star picket to any solid surface — coming soon",
  img: null, fit: "contain", // [PRODUCT IMAGE PLACEHOLDER] — nav renders a neutral slot until supplied
};
export const STEEL_ANGLE_ITEM = {
  to: "/products/pre-drilled-galvanised-angle", name: "Pre-Drilled Galvanised Steel Angle",
  note: "Cut-to-length, pre-drilled angle",
};
export const ALL_PRODUCTS_ITEM = {
  to: "/products", name: "All products", note: "The full StakeTek range",
};
export const GUIDES_ITEM = {
  to: "/blog", name: "Guides", note: "How-tos and buying guides",
};
export const BUILD_IDEAS_ITEM = {
  to: "/applications", name: "Build ideas", note: "What you can build with the system",
};

// Brackets mega-menu — the core range and primary conversion path. The
// concrete-mount base sits here with the slide-ons: it's the bracket that puts
// the picket on concrete. Kits live under Components & accessories.
export const MENU_BRACKETS = [
  SUPPORTED_BRACKET,
  FLAT_BRACKET_ITEM,
  BULK_PACKS_ITEM,
  CONCRETE_MOUNT_ITEM,
];

// Category-led "Products" mega-menu — grouped into four scannable columns so a
// growing range stays organised. Brackets stays the hero (its own menu + primary
// CTA); this organises everything, including the new components line.
export const MENU_PRODUCT_GROUPS = [
  { heading: "Brackets", items: [SUPPORTED_BRACKET, FLAT_BRACKET_ITEM, BULK_PACKS_ITEM, CONCRETE_MOUNT_ITEM] },
  { heading: "Fence extensions", items: [FENCE_EXTENSIONS_ITEM, ANIMAL_EXTENSIONS_COLLECTION_ITEM] },
  { heading: "Components & accessories", items: [INSTALLATION_KITS_ITEM, STEEL_ANGLE_ITEM] },
  // Guides is deliberately NOT in the menu — Tanner wants it low-key, SEO-only
  // (footer + mobile drawer + sitemap are enough for Google to crawl it).
  { heading: "Posts & more", items: [NOTCHED_POST_ITEM, TUBE_POST_ITEM, ALL_PRODUCTS_ITEM, BUILD_IDEAS_ITEM] },
];

// Products mega-menu — the wider catalogue.
export const MENU_PRODUCTS = [
  SUPPORTED_BRACKET,
  FLAT_BRACKET_ITEM,
  INSTALLATION_KITS_ITEM,
  FENCE_POSTS_ITEM,
  FENCE_EXTENSIONS_ITEM,
  BULK_PACKS_ITEM,
  ANIMAL_EXTENSION_ITEM,
  { to: "/products", name: "View the full range", note: "Everything StakeTek makes", img: "/images/fence-paddock.jpg", fit: "cover" },
];

// The cards on Home "Shop the range" and the Brackets hub. "Choose options" is
// kept for cards that open a configurator; everything else names where it goes.
export const RANGE_CARDS = [
  {
    to: "/products/supported-bracket", code: "ST-BR-SO-GAL", tag: "Fits Y-profile pickets",
    name: "Supported Slide-On Bracket", cta: "Choose options",
    note: "2.5 mm Duragal angle on a 2.0 mm gal sleeve, with a timber support lip.",
    ...fromTiers(PREMIUM_TIERS),
    img: "/images/staketek-supported-bracket-studio-gal-angled.jpg", fit: "contain", bg: "#FFFFFF",
  },
  {
    to: "/products/flat-bracket", code: "ST-BR-FL-GAL", tag: "Fits Y-profile pickets",
    name: "Flat Slide-On Bracket", cta: "Choose options",
    note: "Flat fixing face, no support lip.",
    ...fromTiers(FLAT_TIERS),
    img: "/images/staketek-flat-bracket-white-1.jpg", fit: "contain", bg: "#FFFFFF",
  },
  {
    to: "/products/installation-kits", code: "ST-KIT-STD", tag: "Add-on",
    name: "Installation Kits", cta: "Shop kits",
    note: "Matched screws, bolts and nuts counted to your pack size.",
    price: "From $1.50", unit: "per bracket kit",
    img: "/images/kit-install-tight.jpg", fit: "cover", bg: "#17191A",
  },
  {
    to: "/posts", code: "ST-PST-TUBE", tag: "Standalone range",
    name: "Tube-Type Fence Posts", cta: "Enquire",
    note: "Galvanised tube pickets, six lengths, Gal Silver or Black.",
    price: "By enquiry", unit: "six lengths",
    img: "/images/fence-bottlebrush.jpg", fit: "cover", bg: "#17191A",
  },
  {
    to: "/products/animal-hurdle", code: "ST-EXT-27", tag: "Standalone range",
    name: "27° Animal Extension", cta: "Enquire",
    note: "Adds height along the top course, angled outward.",
    price: "By enquiry", unit: "made to order",
    img: "/images/studio-post-animal-extension.jpg", fit: "cover", bg: "#17191A",
  },
  {
    // Automatic volume pricing, so it goes to the bulk packs page, not Trade.
    to: BULK_PACKS_ITEM.to, code: "ST-BULK", tag: "Volume pricing",
    name: "Bulk Packs", cta: "Shop bulk packs",
    note: "The price per bracket drops automatically as the quantity goes up.",
    price: `Down to ${formatAUD(PREMIUM_TIERS[PREMIUM_TIERS.length - 1].unit)}`, unit: "a bracket",
    img: BULK_PACKS_ITEM.img, fit: "cover", bg: "#17191A",
  },
];

// Fence Height Extensions card for the range grid. Links to the collection where
// the customer chooses galvanised or aluminium.
// NOTE: img is a neutral scenic fence photo until real product photography lands
// — swap `img` for the real extension product shot at launch.
export const FENCE_EXTENSIONS_CARD = {
  to: "/products/fence-height-extensions", code: "ST-EXT-FHE", tag: "Galvanised or aluminium",
  name: "Fence Extensions & Joiners", cta: "Choose material",
  note: "Pre-drilled slide-over sleeves that raise an existing star picket fence, or join another picket. No welding.",
  price: `From ${formatAUD(EXTENSION_FROM)}`, unit: "each",
  img: "/images/staketek-fence-extension-installed-1.jpg", fit: "cover", bg: "#17191A",
};

// Anchor Base Bracket card for the range grid. Price/unit auto-fill the
// moment CONCRETE_MOUNT.price is set in data/concreteMount.js. No photo yet, so
// `img` is null — RangeGrid renders an honest photo slot until one is supplied.
export const CONCRETE_MOUNT_CARD = {
  to: CONCRETE_MOUNT.path, code: "ST-BR-ANCHOR", tag: "Coming soon · bracket range",
  name: "Anchor Base Bracket", cta: "View product",
  note: "Anchors a star picket to concrete, roofs or hard ground — build where a picket can't be driven in.",
  // Price auto-fills from data/concreteMount.js the moment it's confirmed.
  price: CONCRETE_MOUNT.price != null ? `$${CONCRETE_MOUNT.price.toFixed(2)}` : "New",
  unit: CONCRETE_MOUNT.price != null ? "each" : "see details",
  // Derives from the product's photo set — add photos in data/concreteMount.js.
  img: CONCRETE_MOUNT.images?.[0]?.src ?? null, fit: "cover", bg: "#17191A",
};

// Animal Extensions card for the range grid — links to the collection that
// covers BOTH the star-picket and 27° versions.
export const ANIMAL_EXTENSIONS_CARD = {
  to: "/products/animal-extensions", code: "ST-EXT-ANIMAL", tag: "Animal exclusion",
  name: "Animal Extensions", cta: "View range",
  note: "Angle the fence top out to help keep out predators and feral animals — star picket or tube.",
  price: "From $10.00", unit: "each",
  img: "/images/staketek-star-picket-animal-extension-1.jpg", fit: "cover", bg: "#17191A",
};

// Homepage "Shop the range" — the SINGLE range section (no separate category
// band). Brackets first, then extensions, then bulk packs and the tube-type
// posts, which are enquiry-only so they sit last.
const findRangeCard = (to) => RANGE_CARDS.find((c) => c.to === to);
// The concrete-mount base is deliberately NOT here — it's a coming-soon venture
// (Tanner, 2026-08-21) and stays off the homepage until it launches. It remains
// reachable via the Brackets hub, Products index and nav menu.
export const HOME_RANGE_CARDS = [
  findRangeCard("/products/supported-bracket"),
  findRangeCard("/products/flat-bracket"),
  FENCE_EXTENSIONS_CARD,
  ANIMAL_EXTENSIONS_CARD,
  findRangeCard(BULK_PACKS_ITEM.to),
  findRangeCard("/posts"),
];
